/*
 * Copyright Deskshare Inc.
 * All rights reserved.
 *
 * This software is proprietary and confidential.
 * Unauthorized copying, distribution, or use is strictly prohibited.
 */

/**
 * auth-client.ts
 *
 * Allauth headless HTTP client + React hook layer.
 *
 * Allauth headless base path: /_allauth/app/v1
 *
 * Session tokens are returned in body.meta.session_token (new sessions — e.g. OTP)
 * or the X-Session-Token response header (existing sessions). Both are stored in
 * localStorage and sent back via X-Session-Token on every subsequent request.
 *
 * Google OAuth uses allauth's browser-mode social login (form POST to
 * /_allauth/browser/v1/auth/provider/redirect). App-mode provider/redirect
 * is for native apps — it does not handle browser redirect flows.
 */

import { useEffect, useRef, useState } from "react";
import { configuration } from "./configuration";
import i18n from "@/i18n";

const BASE = configuration.backendURL;
const ALLAUTH_BASE = `${BASE}/_allauth/app/v1`;
const SESSION_KEY = "ds_session_token";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface AuthUser {
  id: string;
  email: string;
  name: string;
  emailVerified: boolean;
  image: string | null;
}

export interface AuthSession {
  token: string;
  userId: string;
}

export interface AuthResult<T = void> {
  data?: T;
  error?: { message: string; status: number; param?: string };
}

export interface SignInResult {
  user: AuthUser;
  session: AuthSession;
  meta?: { is_authenticated: boolean };
}

export interface SessionResult {
  user: AuthUser;
  session: AuthSession;
}

// ---------------------------------------------------------------------------
// Token storage
// ---------------------------------------------------------------------------

export function getSessionToken(): string | null {
  return localStorage.getItem(SESSION_KEY);
}

function storeSessionToken(token: string): void {
  localStorage.setItem(SESSION_KEY, token);
}

function clearSessionToken(): void {
  localStorage.removeItem(SESSION_KEY);
}

// ---------------------------------------------------------------------------
// Core fetch wrapper
// ---------------------------------------------------------------------------

const FETCH_TIMEOUT_MS = 30_000;

async function allauthFetch<T>(
  path: string,
  options: RequestInit = {},
  _retried = false,
): Promise<AuthResult<T>> {
  const token = getSessionToken();
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    Accept: "application/json",
    // S-2: Send the site's active language so LocaleMiddleware picks the
    // correct locale for transactional emails (sign-in code, password reset).
    // i18n.language is restricted to the supported set (en/de/es/fr/it/pt)
    // via supportedLngs, which maps 1:1 to the backend LANGUAGES setting —
    // no extra normalization needed.
    "Accept-Language": i18n.language || "en",
    ...(token ? { "X-Session-Token": token } : {}),
    ...(options.headers as Record<string, string>),
  };

  // Ensure CSRF token header for unsafe methods when credentials are included.
  const method = ((options.method ?? "GET") as string).toUpperCase();
  if (method !== "GET" && !headers["X-CSRFToken"]) {
    // Try cookie first (same-origin dev), otherwise call the CSRF endpoint.
    const existing = getCsrfCookie();
    const csrf = existing || (await fetchCsrfToken());
    if (csrf) headers["X-CSRFToken"] = csrf;
  }

  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), FETCH_TIMEOUT_MS);

  try {
    const response = await fetch(`${ALLAUTH_BASE}${path}`, {
      ...options,
      headers,
      credentials: "include",
      signal: controller.signal,
    });

    // Parse body first — token lives in body.meta.session_token for new sessions
    // (e.g. OTP request) and in the X-Session-Token header for existing sessions.
    const body =
      response.ok || response.status < 500
        ? await response.json().catch(() => ({}))
        : {};

    // Dev: log non-OK responses for debugging. Must happen AFTER response.json()
    // because reading response.text() first would consume the stream and make
    // response.json() return {}, breaking the 401+flows detection below.
    if (process.env.NODE_ENV !== "production" && !response.ok) {
      console.debug("allauthFetch non-ok response:", response.status, JSON.stringify(body));
    }

    // Primary: body.meta.session_token. Fallback: header (existing/modified sessions).
    const metaToken = (body as { meta?: { session_token?: string } })?.meta
      ?.session_token;
    const headerToken = response.headers.get("X-Session-Token");
    const newToken = metaToken ?? headerToken;
    // N1: Never save a token returned by the logout response. The server's
    // DELETE /auth/session reply can carry a fresh signed-out token in the
    // same header. Saving it re-populates localStorage right after we cleared
    // it, causing the login screen to flicker back to a signed-in look (S1).
    const isLogout =
      path === "/auth/session" &&
      (options.method ?? "GET").toUpperCase() === "DELETE";
    if (newToken && !isLogout) {
      storeSessionToken(newToken);
    }

    if (!response.ok) {
      // 410 Gone: the session token no longer exists on the server.
      // Clear the stale token and retry once as a clean unauthenticated request.
      if (response.status === 410 && !_retried) {
        clearSessionToken();
        return allauthFetch<T>(path, options, true);
      }

      // 409 Conflict on /auth/login: a pending-OTP session is blocking a
      // new password login. Clear the stale token and retry once.
      // T2: Never retry 409 on /auth/password/reset — the reset key is a
      // one-time token. A retry can consume the key on the first attempt and
      // fail on the second, showing "expired or invalid" after a correct
      // single submission. resetPassword() already clears the token before its
      // single POST so any stale-session 409 is handled the safe way.
      if (response.status === 409 && !_retried && path === "/auth/login") {
        clearSessionToken();
        return allauthFetch<T>(path, options, true);
      }

      const b = body as Record<string, unknown>;

      // Allauth headless contract — 401 with a flows[] array signals a
      // pending authentication state (OTP sent, MFA required, etc.) — NOT a failure.
      // The flows array can sit at the top level or inside a data key.
      const inner = b?.data as Record<string, unknown> | undefined;
      const flows = Array.isArray(b?.flows)
        ? b.flows
        : /* c8 ignore next 2 -- nested ternary; "flows inside data key" test covers
             this path but c8 cannot track inner branch of a nested ternary */
          Array.isArray(inner?.flows)
          ? inner.flows
          : null;

      /* c8 ignore next 3 -- 401+flows branch is tested but c8 undercounts branches
         when a ternary result immediately feeds the if condition */
      if (response.status === 401 && flows) {
        return { data: body as T };
      }

      const message =
        (b?.errors as Array<{ message: string }>)?.[0]?.message ??
        (b?.error as string) ??
        (b?.detail as string) ??
        "An error occurred. Please try again.";
      // Allauth includes a structured "param" field on validation errors
      // (e.g. param="password" for password-rule failures on password reset).
      // Carry it through so callers can key on it rather than parsing the message.
      const param = (b?.errors as Array<{ param?: string }>)?.[0]?.param;
      return {
        error: {
          message,
          status: response.status,
          ...(param ? { param } : {}),
        },
      };
    }

    return { data: body as T };
  } catch (err) {
    if ((err as Error).name === "AbortError") {
      return {
        error: {
          message: "Request timed out. Please check your connection.",
          status: 0,
        },
      };
    }
    return {
      error: {
        message: "Network error. Please check your connection.",
        status: 0,
      },
    };
  } finally {
    clearTimeout(timer);
  }
}

// ---------------------------------------------------------------------------
// Session fetch (shared by hook + one-shot callers)
// ---------------------------------------------------------------------------

async function fetchSession(): Promise<AuthResult<SessionResult>> {
  const raw = await allauthFetch<Record<string, unknown>>("/auth/session", {
    method: "GET",
  });

  if (raw.error) return { error: raw.error };
  const body = raw.data as Record<string, unknown>;

  const inner = body?.data as Record<string, unknown> | undefined;
  const meta = body?.meta as Record<string, unknown> | undefined;
  const user = inner?.user as AuthUser | undefined;
  // Token is in meta.session_token — not in data.sessions[].
  // data.sessions belongs to allauth.usersessions, which is not installed.
  const token =
    (meta?.session_token as string | undefined) ?? getSessionToken();

  if (!user || !token) {
    return { error: { message: "No active session.", status: 401 } };
  }

  return { data: { user, session: { token, userId: String(user.id ?? "") } } };
}

// ---------------------------------------------------------------------------
// useSession hook
// ---------------------------------------------------------------------------

export interface UseSessionReturn {
  data: SessionResult | null;
  isPending: boolean;
  error: { message: string; status: number } | null;
}

export function useSession(): UseSessionReturn {
  const [state, setState] = useState<UseSessionReturn>({
    data: null,
    isPending: true,
    error: null,
  });

  const fetched = useRef(false);

  useEffect(() => {
    if (fetched.current) return;
    fetched.current = true;

    let cancelled = false;

    fetchSession().then((result) => {
      if (cancelled) return;
      if (result.data) {
        setState({ data: result.data, isPending: false, error: null });
      } else {
        setState({
          data: null,
          isPending: false,
          error: result.error ?? null,
        });
      }
    });

    return () => {
      cancelled = true;
      // Do not reset fetched.current here. The session fetch is idempotent,
      // but resetting the guard in cleanup causes a duplicate network call on
      // every React Strict Mode remount and keeps the code inconsistent with
      // the single-fire pattern used in one-time-code auth flows.
    };
  }, []);

  return state;
}

// ---------------------------------------------------------------------------
// Core auth functions
// ---------------------------------------------------------------------------

/**
 * T4: Drop any prior session token and Django browser cookie before a new
 * sign-in attempt. Called by every sign-in entry point that starts fresh
 * (password login, OTP request). Not called on the OTP confirm step — that
 * step relies on the pending-login token from the request step.
 *
 * Keeps sign-in consistent across all three entry paths:
 *   - Password login    → prepareForSignIn() clears token + browser cookie
 *   - OTP request       → prepareForSignIn() + existing clearSessionToken()
 *   - Google OAuth      → already calls browser-logout via signInWithGoogle()
 */
async function prepareForSignIn(): Promise<void> {
  clearSessionToken();
  try {
    await fetch(`${BASE}/api/auth/browser-logout`, {
      method: "POST",
      credentials: "include",
    });
  } catch {
    // Non-fatal — proceed regardless so a network blip doesn't block sign-in.
  }
}

async function signInWithEmail(
  email: string,
  password: string,
): Promise<AuthResult<SignInResult>> {
  // T4: Clear any prior session token and browser cookie before a password login.
  // A leftover session from a previous user can cause the server to authenticate
  // as that user rather than processing the new credentials.
  await prepareForSignIn();
  // allauth headless expects the identifier under the `login` key (username/email).
  // Send both `login` and `email` to satisfy different allauth configurations
  // (some validation rules expect `email` present even when `login` is used).
  return allauthFetch<SignInResult>("/auth/login", {
    method: "POST",
    body: JSON.stringify({ login: email, email, password }),
  });
}

async function signInWithEmailOtp(
  otp: string,
): Promise<AuthResult<SignInResult>> {
  return allauthFetch<SignInResult>("/auth/code/confirm", {
    method: "POST",
    body: JSON.stringify({ code: otp }),
  });
}

async function signOut(): Promise<AuthResult<void>> {
  const result = await allauthFetch<void>("/auth/session", {
    method: "DELETE",
  });
  // Only clear the local token when the server confirms the session was
  // deleted. Clearing unconditionally means a failed DELETE leaves the user
  // appearing logged out locally while the server session is still active —
  // on reload useSession() re-authenticates them silently.
  //
  // Section 7.6: when the server call fails, return the error so the caller
  // can show a retry message (e.g. "Couldn't sign you out — please try again")
  // and keep the sign-out button enabled. The local token is NOT cleared so
  // the session stays valid and the user can retry without being stuck.
  if (!result.error) {
    clearSessionToken();
    // T5: Clear the in-memory JWT cache immediately so a subsequent user
    // cannot be served User A's access token for up to 13 minutes after
    // User A signs out. The lazy clear inside getJsonWebToken() runs only
    // on the NEXT call, leaving the stale token in memory in the meantime.
    const { clearJwtCache } = await import("./http-client");
    clearJwtCache();
  }
  return result;
}

export async function getSession(): Promise<AuthResult<SessionResult>> {
  return fetchSession();
}

async function sendVerificationEmail(email: string): Promise<AuthResult> {
  // T4: A returning visitor may still hold a session token and a browser cookie
  // from an earlier visit. allauth rejects a fresh login-by-code request that
  // carries a valid session (400, no email sent). prepareForSignIn() clears both
  // the local token and the browser-side Django session cookie, so the request
  // is treated as the anonymous login-by-code request it is meant to be.
  // This also heals the "Resend code" fallback path: when resendLoginCode()
  // returns 409 (session stage expired), it falls back here, and that request
  // would fail the same way without this clear.
  await prepareForSignIn();
  // Send JSON body; headless allauth in this project expects JSON payloads.
  return allauthFetch("/auth/code/request", {
    method: "POST",
    body: JSON.stringify({ email }),
  });
}

async function forgotPassword(email: string): Promise<AuthResult> {
  return allauthFetch("/auth/password/request", {
    method: "POST",
    body: JSON.stringify({ email }),
  });
}

// Resend the active login code via POST /auth/code/resend (allauth 65.16+).
// Returns 409 if the session stage has expired (caller should fall back to sendVerificationEmail), 429 if rate-limited.
async function resendLoginCode(): Promise<AuthResult> {
  return allauthFetch("/auth/code/resend", {
    method: "POST",
  });
}

async function resetPassword(
  key: string,
  password: string,
): Promise<AuthResult> {
  // A stale session token from a prior login attempt can block the anonymous
  // password-reset flow (allauth may return 400/409). Drop it before posting.
  clearSessionToken();
  return allauthFetch("/auth/password/reset", {
    method: "POST",
    body: JSON.stringify({ key, password }),
  });
}

async function changePassword(
  currentPassword: string,
  newPassword: string,
): Promise<AuthResult> {
  return allauthFetch("/auth/password/change", {
    method: "POST",
    body: JSON.stringify({
      current_password: currentPassword,
      new_password: newPassword,
    }),
  });
}

/**
 * Fetch CSRF token from the backend's /api/csrf/ endpoint.
 *
 * In cross-origin deployments (frontend on auth.deskshare.com, backend on
 * api.auth.deskshare.com), JavaScript cannot read cookies from a different
 * subdomain. getCsrfCookie() returns an empty string, and Django rejects the
 * Google OAuth form POST with 403.
 *
 * This function checks the cookie first (works in same-origin dev) and falls
 * back to the GET /api/csrf/ endpoint (works in all environments). The
 * endpoint sets the ds_csrf cookie via @ensure_csrf_cookie and returns the
 * token in the response body so we can read it cross-origin.
 */
function getCsrfCookie(): string {
  const match = document.cookie.match(/(?:^|;\s*)ds_csrf=([^;]+)/);
  return match ? decodeURIComponent(match[1]) : "";
}

async function fetchCsrfToken(): Promise<string> {
  const cached = getCsrfCookie();
  if (cached) return cached;
  try {
    const res = await fetch(`${BASE}/api/csrf/`, {
      method: "GET",
      credentials: "include",
    });
    const data = (await res.json()) as { csrfToken?: string };
    return data.csrfToken ?? "";
  } catch {
    return "";
  }
}

/** Google sign-in via browser-mode social login (form POST to allauth's provider/redirect endpoint). */
async function signInWithGoogle(callbackUrl?: string): Promise<void> {
  // Flush the Django browser session (sessionid cookie) before starting the
  // OAuth flow. If a previous email/password login left a valid sessionid,
  // allauth's provider/redirect endpoint considers the user already
  // authenticated and short-circuits straight to callback_url without ever
  // redirecting to Google — the user never sees the Google sign-in screen.
  //
  // /api/auth/browser-logout is a @csrf_exempt POST that calls Django's
  // logout() directly — far more reliable than the allauth headless DELETE
  // endpoint which requires an authenticated session and a matching CSRF token
  // (causing 401s when the token/session mismatch).
  try {
    await fetch(`${BASE}/api/auth/browser-logout`, {
      method: "POST",
      credentials: "include",
    });
  } catch {
    // Non-fatal — proceed with the OAuth flow regardless.
  }

  const csrfToken = await fetchCsrfToken();
  const redirectUrl = callbackUrl ?? `${window.location.origin}/auth/callback`;

  const form = document.createElement("form");
  form.method = "POST";
  form.action = `${BASE}/_allauth/browser/v1/auth/provider/redirect`;
  form.style.display = "none";

  const fields: Record<string, string> = {
    provider: "google",
    callback_url: redirectUrl,
    process: "login",
    csrfmiddlewaretoken: csrfToken,
  };

  for (const [name, value] of Object.entries(fields)) {
    const input = document.createElement("input");
    input.type = "hidden";
    input.name = name;
    input.value = value;
    form.appendChild(input);
  }

  document.body.appendChild(form);
  form.submit();
}

function isAuthenticated(): boolean {
  return !!getSessionToken();
}

// ---------------------------------------------------------------------------
// authClient — better-auth compatible shape
// ---------------------------------------------------------------------------

export const authClient = {
  useSession,
  signOut,
  getSession,
  sendVerificationEmail,
  forgotPassword,
  resetPassword,
  changePassword,
  signInWithGoogle,
  isAuthenticated,
  resendLoginCode,

  signIn: {
    email: ({
      email,
      password,
    }: {
      email: string;
      password: string;
      callbackURL?: string;
    }): Promise<AuthResult<SignInResult>> => signInWithEmail(email, password),

    emailOtp: ({
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      email: _email,       // intentionally unused — public API shape preserved; callers pass email but only otp is sent
      otp,
    }: {
      email: string;
      otp: string;
    }): Promise<AuthResult<SignInResult>> => signInWithEmailOtp(otp),

    social: ({
      provider,
      callbackURL,
    }: {
      provider: string;
      callbackURL?: string;
    }): Promise<AuthResult<void>> => {
      if (provider === "google") {
        // signInWithGoogle is async — return the Promise directly.
        return signInWithGoogle(callbackURL).then(
          () => ({}) as AuthResult<void>,
        );
      }
      return Promise.resolve({
        error: {
          message: `Social provider "${provider}" is not configured.`,
          status: 400,
        },
      } as AuthResult<void>);
    },
  },

  emailOtp: {
    sendVerificationOtp: ({
      email,
    }: {
      email: string;
      // type parameter intentionally omitted: only one code flow (sign-in)
      // exists. Wire it through here if additional code types are added.
    }): Promise<AuthResult> => sendVerificationEmail(email),
  },
};
